import React, {useCallback, useEffect, useRef, useState} from 'react';
import {StyleSheet} from 'react-native';
import {Box, Button, VStack, Text, Skeleton, useTheme} from 'native-base';
import {createIconSetFromFontello} from 'react-native-vector-icons';
import APIAction from '../../../Actions/APIAction';
import ProjectShift from './ProjectShift';
import Trans from '../../Components/Trans';

import lineawesome from '../../../Assets/Fontello/line-awesome-config.json';
import main from '../../../Assets/Styles/main.json';
import ProjectShiftEditPane from './ProjectShiftEditPane';
import GeneralAction from '../../../Actions/GeneralAction';
import TriggerAction from '../../../Actions/TriggerAction';
import {v4 as uuidv4} from 'uuid';

const Icon = createIconSetFromFontello(lineawesome);
const mainStyle = StyleSheet.create(main);

const ProjectShiftList = props => {
  // Destructure props for ESLint react-hooks/exhaustive-deps
  const {project, onShiftsEmpty, selectedDepartments, onVisibilityChange, style} = props;

  const firstLoad = useRef(true),
    [show, setShow] = useState(true),
    [showCreate, setShowCreate] = useState(false),
    showCreateFirst = useRef(false),
    [shiftElements, setShiftElements] = useState(null),
    lastVisibility = useRef(true);

  const {colors} = useTheme();

  const shiftsAreEmpty = useCallback(isTrue => {
    onShiftsEmpty(isTrue);
  }, [onShiftsEmpty]);

  const loadData = useCallback(() => {
    const init = async () => {
      let abortController = TriggerAction.getTigger('projectScreenCancel');
      //get shifts
      let shifts = await APIAction.request({
        method: 'GET',
        url: '/api/shifts/find_by/project',
        params: {
          projectId: project.id,
        },
        cache: false,
        abortController: abortController,
      });
      if (shifts === false) return;
      shifts = shifts['hydra:member'];

      // Get selected departments
      const deptFilter = selectedDepartments || [];

      //sort shifts
      let sortedShifts = {};
      for (let shift of shifts) {
        let department = 'none';
        if (shift.department) department = shift.department.name;
        if (!(department in sortedShifts)) sortedShifts[department] = {};
        let startOn = GeneralAction.formatDate(shift.startOn);
        if (!(startOn in sortedShifts[department]))
          sortedShifts[department][startOn] = [];
        sortedShifts[department][startOn].push(shift);
      }

      // Filter departments based on selection
      let filteredDepartments = Object.keys(sortedShifts);
      if (deptFilter.length > 0) {
        filteredDepartments = filteredDepartments.filter(dept => {
          // Match department name (case-insensitive) or 'none' for no department
          const deptLower = dept.toLowerCase();
          return deptFilter.some(selected => {
            const selectedLower = selected.toLowerCase();
            return selectedLower === deptLower;
          });
        });
      }

      // Notify parent about visibility
      const hasVisibleShifts = filteredDepartments.length > 0 || deptFilter.length === 0;
      if (onVisibilityChange && lastVisibility.current !== hasVisibleShifts) {
        lastVisibility.current = hasVisibleShifts;
        onVisibilityChange(hasVisibleShifts);
      }

      //create new shift elements
      let newShiftElements = [];
      for (let [department, sorted] of Object.entries(sortedShifts)) {
        // Skip departments not in the filter (if filter is active)
        if (deptFilter.length > 0 && !filteredDepartments.includes(department)) {
          continue;
        }

        //department title
        newShiftElements.push(
          <Text key={`dept-${department}`} opacity={0.5} marginBottom={2}>
            {department.toUpperCase()}
          </Text>,
        );
        for (let [date, shiftArray] of Object.entries(sorted)) {
          for (let shift of shiftArray) {
            newShiftElements.push(
              <ProjectShift
                key={uuidv4()}
                paddingLeft={2}
                paddingBottom={2}
                shift={shift}
                project={project}
                readOnly={props.readOnly}
                onUpdate={() => {
                  init();
                }}
                shiftsAreEmpty={shiftsAreEmpty}
              />,
            );
          }
        }
      }

      setShiftElements(newShiftElements);
    };
    init();
  }, [project, shiftsAreEmpty, selectedDepartments, onVisibilityChange, props.readOnly]);

  useEffect(() => {
    if (firstLoad.current && show) {
      firstLoad.current = false;
      loadData();
    }
  }, [loadData, show]);

  // Re-run loadData when selectedDepartments change
  useEffect(() => {
    if (!firstLoad.current) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartments]);

  return (
    <>
      <Box style={style}>
        {/* <Button
                    variant={'link'}
                    onPress={() => setShow(!show)}
                >
                    <Icon size={20} name={show ? 'chevron-up' : 'chevron-down'} />
                </Button> */}

        <Box display={show ? 'flex' : 'none'}>
          {shiftElements !== null ? (
            <VStack space={2} alignItems={'flex-start'}>
              {shiftElements.length > 0 && (
                <VStack
                  style={{
                    backgroundColor: '#edf5f9',
                    width: '100%',
                    padding: 5,
                    borderRadius: 5,
                  }}>
                  {shiftElements}
                </VStack>
              )}
              {!props.readOnly && new Date(project.endDate) >=
                new Date(Date.now() - 86400000) && ( // It should stay visible on the last day as well
                <Button
                  w={'100%'}
                  p={1}
                  leftIcon={
                    <Icon
                      style={{color: colors['secondary']['600']}}
                      size={16}
                      name={'plus'}
                    />
                  }
                  borderRadius={'md'}
                  colorScheme={'secondary'}
                  variant={'outline'}
                  onPress={() => {
                    showCreateFirst.current = true;
                    setShowCreate(true);
                  }}>
                  <Text fontSize={'xs'} color={colors['secondary']['600']}>
                    <Trans>Add shift</Trans>
                  </Text>
                </Button>
              )}
            </VStack>
          ) : (
            <VStack space={2}>
              <Skeleton.Text lines={2} />
              <Skeleton.Text lines={2} />
            </VStack>
          )}
        </Box>
      </Box>
      {showCreateFirst && (
        <ProjectShiftEditPane
          open={showCreate}
          project={project}
          onClose={() => {
            setShowCreate(false);
          }}
          reload={() => {
            loadData();
          }}
        />
      )}
    </>
  );
};

export default ProjectShiftList;
